'use client';

import { useState, useEffect, useRef } from 'react';
import TradingPairSelector, { DEFAULT_PAIR, TradingPair } from './TradingPairSelector';
import ChartPanel from './ChartPanel';
import OrderBook from './OrderBook';
import TradingPanel from './TradingPanel';
import TradeHistory from './TradeHistory';
import OpenPositions from './OpenPositions';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import { useHyperliquid } from '@/lib/hyperliquid/useHyperliquid';
import ActiveOrdersPanel from './ActiveOrdersPanel';
import { useWallet } from '@/lib/wallet/WalletContext';

export interface TradeMarker {
  id: string;
  side: 'Buy' | 'Sell';
  price: number;
  size: number;
  symbol: string;
  timestamp: number;
}

export interface SLTPState {
  slEnabled: boolean;
  slPrice: string;
  tpEnabled: boolean;
  tpPrice: string;
}

// Hook to flash a value when it changes
function useFlash(value: string | undefined): 'green' | 'red' | null {
  const [flash, setFlash] = useState<'green' | 'red' | null>(null);
  const prevRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevRef.current === undefined) {
      prevRef.current = value;
      return;
    }
    if (value !== undefined && value !== prevRef.current) {
      const prev = parseFloat((prevRef.current ?? '0').replace(/,/g, ''));
      const curr = parseFloat((value ?? '0').replace(/,/g, ''));
      const dir = curr >= prev ? 'green' : 'red';
      setFlash(dir);
      const t = setTimeout(() => setFlash(null), 600);
      prevRef.current = value;
      return () => clearTimeout(t);
    }
    prevRef.current = value;
  }, [value]);

  return flash;
}

// Stat cell with flash animation
function StatCell({ label, value, prefix = '$' }: { label: string; value: string | undefined; prefix?: string }) {
  const flash = useFlash(value);
  const flashColor =
    flash === 'green' ? 'rgba(34,197,94,0.18)' :
    flash === 'red' ? 'rgba(239,68,68,0.18)' :
    'transparent';
  const textColor =
    flash === 'green' ? '#22C55E' :
    flash === 'red'? '#EF4444' : '#F1F5F9';

  return (
    <div className="flex flex-col gap-0.5 px-3 py-1.5 border-l border-[#2a2a2a]">
      <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{label}</span>
      <span
        className="text-sm font-mono font-semibold tabular-nums transition-colors duration-300"
        style={{
          color: textColor,
          background: flashColor,
          transition: 'color 0.3s, background 0.3s',
          padding: '0 2px',
        }}
      >
        {value ? `${prefix}${value}` : <span className="text-[#4a4a4a]">{prefix}—</span>}
      </span>
    </div>
  );
}

// Mobile compact spread row for order book
function MobileSpreadRow({ midPrice, spread, asks, bids }: { midPrice: string; spread: string; asks: any[]; bids: any[] }) {
  const bestAsk = asks[0];
  const bestBid = bids[0];
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-[#1a1a1a] border-b border-[#2a2a2a]">
      <div className="flex flex-col">
        <span className="text-[9px] text-[#94A3B8] uppercase tracking-wider">Best Bid</span>
        <span className="text-xs font-mono text-[#22C55E] tabular-nums">{bestBid?.price ?? '—'}</span>
      </div>
      <div className="flex flex-col items-center">
        <span className="text-[9px] text-[#94A3B8]">Spread</span>
        <span className="text-xs font-mono text-[#4c6ef4] tabular-nums">${spread}</span>
        <span className="text-[9px] font-mono text-[#F1F5F9] tabular-nums">${midPrice}</span>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-[9px] text-[#94A3B8] uppercase tracking-wider">Best Ask</span>
        <span className="text-xs font-mono text-[#EF4444] tabular-nums">{bestAsk?.price ?? '—'}</span>
      </div>
    </div>
  );
}

export default function TradingDashboardInteractive() {
  const [selectedPair, setSelectedPair] = useState<TradingPair>(DEFAULT_PAIR);
  const [activeTab, setActiveTab] = useState<'positions' | 'history'>('positions');
  const [activeTimeframe, setActiveTimeframe] = useState('1h');
  const [isHydrated, setIsHydrated] = useState(false);
  const [orderBookPrice, setOrderBookPrice] = useState<string | undefined>(undefined);
  const [orderBookSize, setOrderBookSize] = useState<string | undefined>(undefined);
  const [orderBookSide, setOrderBookSide] = useState<'Buy' | 'Sell' | undefined>(undefined);

  // Mobile-specific state
  const [mobileTradingSheetOpen, setMobileTradingSheetOpen] = useState(false);
  const [mobileTradingSide, setMobileTradingSide] = useState<'Buy' | 'Sell'>('Buy');
  const [mobilePairModalOpen, setMobilePairModalOpen] = useState(false);

  const { usdcBalance } = useWallet();

  const [sltp, setSltp] = useState<SLTPState>({
    slEnabled: false,
    slPrice: '',
    tpEnabled: false,
    tpPrice: '',
  });

  const [tradeMarkers, setTradeMarkers] = useState<TradeMarker[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Prevent body scroll when mobile sheet is open
  useEffect(() => {
    if (mobileTradingSheetOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileTradingSheetOpen]);

  const handleOrderBookRowClick = (price: string, size: string, side: 'Buy' | 'Sell') => {
    setOrderBookPrice(price);
    setOrderBookSize(size);
    setOrderBookSide(side);
  };

  const handleTradePlaced = (marker: TradeMarker) => {
    setTradeMarkers(prev => [...prev, marker]);
  };

  const handleCancelSL = () => {
    setSltp(prev => ({ ...prev, slEnabled: false, slPrice: '' }));
  };

  const handleCancelTP = () => {
    setSltp(prev => ({ ...prev, tpEnabled: false, tpPrice: '' }));
  };

  const {
    bids,
    asks,
    midPrice,
    spread,
    trades,
    marketStats,
    isConnected,
  } = useHyperliquid(selectedPair.symbol, activeTimeframe);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#4c6ef4] border-t-transparent animate-spin" />
          <span className="text-[#94A3B8] text-sm">Loading Trading Dashboard...</span>
        </div>
      </div>
    );
  }

  const displayPrice = midPrice || selectedPair.price;
  const basePrice = parseFloat(displayPrice.replace(',', ''));

  const formattedPrice = basePrice > 0
    ? basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : displayPrice;

  const change24h = marketStats?.change24h ?? selectedPair.change;
  const isPositive = marketStats ? marketStats.positive : selectedPair.positive;

  return (
    <div className="h-screen bg-[#141414] flex flex-col overflow-hidden">
      <Header />

      {/* ─── DESKTOP LAYOUT ─── */}
      <div className="hidden md:flex flex-col flex-1 min-h-0 overflow-hidden mt-[88px]">
        {/* Top bar with pair selector */}
        <div className="px-3 lg:px-4 py-2 bg-[#1a1a1a] border-b border-[#2a2a2a] flex items-center gap-4 flex-wrap shrink-0">
          <TradingPairSelector
            selectedPair={selectedPair}
            onSelectPair={setSelectedPair}
            liveStats={marketStats}
          />

          <div className="flex items-center gap-0 flex-wrap">
            <div className="flex flex-col gap-0.5 px-3 py-1.5">
              <PriceCell price={formattedPrice} />
              <span className={`text-xs font-mono font-semibold tabular-nums ${isPositive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                {change24h}
              </span>
            </div>
            <StatCell label="24H HIGH" value={marketStats?.high24h} />
            <StatCell label="24H LOW" value={marketStats?.low24h} />
            <StatCell label="24H VOLUME" value={marketStats?.dayVolume} prefix="$" />
          </div>

          <div className="ml-auto hidden xl:flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#242424] border border-[#2a2a2a]">
              <Icon name="SignalIcon" size={12} className="text-[#22C55E]" />
              <span className="text-xs text-[#94A3B8]">Open Interest:</span>
              <span className="text-xs font-mono text-[#F1F5F9] tabular-nums">{marketStats?.openInterest ?? '$4.28B'}</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#242424] border border-[#2a2a2a]">
              <Icon name="ArrowsRightLeftIcon" size={12} className="text-[#4c6ef4]" />
              <span className="text-xs text-[#94A3B8]">Funding:</span>
              <span className={`text-xs font-mono tabular-nums ${marketStats ? (marketStats.positive ? 'text-[#22C55E]' : 'text-[#EF4444]') : 'text-[#22C55E]'}`}>
                {marketStats?.fundingRate ?? '+0.0102%'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#242424] border border-[#2a2a2a]">
              <span className={`w-1.5 h-1.5 ${isConnected ? 'bg-[#22C55E] animate-pulse' : 'bg-[#EF4444]'}`} />
              <span className="text-xs text-[#94A3B8]">{isConnected ? 'Connected' : 'Reconnecting...'}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex flex-col lg:flex-row flex-1 min-h-0">
            <div className="flex-1 min-w-0 border-r border-[#2a2a2a] flex flex-col min-h-0">
              <ChartPanel
                symbol={selectedPair.symbol}
                activeTimeframe={activeTimeframe}
                onTimeframeChange={setActiveTimeframe}
                sltp={sltp}
                tradeMarkers={tradeMarkers}
                currentPrice={basePrice}
                onCancelSL={handleCancelSL}
                onCancelTP={handleCancelTP}
              />
              <ActiveOrdersPanel symbol={selectedPair.symbol} />
            </div>
            <div className="w-full lg:w-[22%] xl:w-[20%] shrink-0 border-r border-[#2a2a2a] flex flex-col min-h-0 overflow-y-auto">
              <OrderBook
                symbol={selectedPair.symbol}
                midPrice={displayPrice}
                bids={bids}
                asks={asks}
                spread={spread}
                isLive={isConnected && bids.length > 0}
                onRowClick={handleOrderBookRowClick}
              />
            </div>
            <div className="w-full lg:w-[22%] xl:w-[20%] shrink-0 flex flex-col min-h-0 overflow-y-auto">
              <TradingPanel
                symbol={selectedPair.symbol}
                currentPrice={displayPrice}
                externalPrice={orderBookPrice}
                externalSize={orderBookSize}
                externalSide={orderBookSide}
                externalOrderType={orderBookPrice ? 'Limit' : undefined}
                sltp={sltp}
                onSltpChange={setSltp}
                onTradePlaced={handleTradePlaced}
                usdcBalance={usdcBalance}
              />
            </div>
          </div>

          <div className="border-t border-[#2a2a2a] shrink-0" style={{ maxHeight: '180px', overflowY: 'auto', background: '#141414' }}>
            <div className="flex items-center border-b border-[#2a2a2a] sticky top-0 z-10" style={{ background: '#1e1e1e' }}>
              {(['positions', 'history'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 text-sm font-medium capitalize transition-all duration-150 border-b-2 -mb-px ${
                    activeTab === tab
                      ? 'text-[#4c6ef4] border-[#4c6ef4] bg-[rgba(76,110,244,0.08)]'
                      : 'text-[#94A3B8] border-transparent hover:text-[#F1F5F9]'
                  }`}
                >
                  {tab === 'positions' ? 'Open Positions' : 'Trade History'}
                </button>
              ))}
            </div>
            <div className="p-0">
              {activeTab === 'positions' ? (
                <OpenPositions />
              ) : (
                <TradeHistory
                  symbol={selectedPair.symbol}
                  basePrice={basePrice}
                  trades={trades}
                  isLive={isConnected && trades.length > 0}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── MOBILE LAYOUT ─── */}
      <div
        className="md:hidden flex flex-col flex-1 min-h-0 overflow-hidden"
        style={{
          marginTop: '56px',
          paddingBottom: 'calc(56px + env(safe-area-inset-bottom))',
          WebkitOverflowScrolling: 'touch' as any,
        }}
      >
        {/* Mobile top bar: pair selector + price */}
        <div
          className="flex items-center justify-between px-3 py-2 bg-[#1a1a1a] border-b border-[#2a2a2a] shrink-0"
          style={{ minHeight: '44px' }}
        >
          {/* Pair selector button — opens full-screen modal */}
          <button
            onClick={() => setMobilePairModalOpen(true)}
            className="flex items-center gap-2 active:opacity-70 transition-opacity"
            style={{ minHeight: '44px' }}
          >
            <span className="text-sm font-bold text-[#F1F5F9]">{selectedPair.symbol}</span>
            <Icon name="ChevronDownIcon" size={14} className="text-[#94A3B8]" />
          </button>

          {/* Price + change */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end">
              <PriceCell price={formattedPrice} />
              <span className={`text-[10px] font-mono tabular-nums ${isPositive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                {change24h}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-[#22C55E] animate-pulse' : 'bg-[#EF4444]'}`} />
            </div>
          </div>
        </div>

        {/* Mobile scrollable content */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ WebkitOverflowScrolling: 'touch' as any }}
        >
          {/* Chart — 40vh */}
          <div style={{ height: '40vh', minHeight: '240px' }} className="border-b border-[#2a2a2a]">
            <ChartPanel
              symbol={selectedPair.symbol}
              activeTimeframe={activeTimeframe}
              onTimeframeChange={setActiveTimeframe}
              sltp={sltp}
              tradeMarkers={tradeMarkers}
              currentPrice={basePrice}
              onCancelSL={handleCancelSL}
              onCancelTP={handleCancelTP}
            />
          </div>

          {/* Compact horizontal spread / order book summary */}
          <MobileSpreadRow midPrice={displayPrice} spread={spread} asks={asks} bids={bids} />

          {/* Active Orders */}
          <div className="border-b border-[#2a2a2a]">
            <ActiveOrdersPanel symbol={selectedPair.symbol} />
          </div>

          {/* Positions / History tabs */}
          <div className="border-b border-[#2a2a2a]">
            <div className="flex items-center border-b border-[#2a2a2a] bg-[#1e1e1e]">
              {(['positions', 'history'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-3 text-sm font-medium capitalize transition-all duration-150 border-b-2 -mb-px ${
                    activeTab === tab
                      ? 'text-[#4c6ef4] border-[#4c6ef4] bg-[rgba(76,110,244,0.08)]'
                      : 'text-[#94A3B8] border-transparent'
                  }`}
                  style={{ minHeight: '44px' }}
                >
                  {tab === 'positions' ? 'Positions' : 'History'}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' as any }}>
              {activeTab === 'positions' ? (
                <OpenPositions />
              ) : (
                <TradeHistory
                  symbol={selectedPair.symbol}
                  basePrice={basePrice}
                  trades={trades}
                  isLive={isConnected && trades.length > 0}
                />
              )}
            </div>
          </div>

          {/* Bottom spacer */}
          <div style={{ height: '80px' }} />
        </div>

        {/* Mobile Buy/Sell sticky bar */}
        <div
          className="shrink-0 flex gap-0 border-t border-[#2a2a2a] bg-[#1a1a1a]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <button
            onClick={() => { setMobileTradingSide('Buy'); setMobileTradingSheetOpen(true); }}
            className="flex-1 py-3.5 text-sm font-bold text-white bg-[#22C55E] hover:bg-[#16a34a] active:bg-[#15803d] transition-colors"
            style={{ minHeight: '52px' }}
          >
            Buy / Long
          </button>
          <button
            onClick={() => { setMobileTradingSide('Sell'); setMobileTradingSheetOpen(true); }}
            className="flex-1 py-3.5 text-sm font-bold text-white bg-[#EF4444] hover:bg-[#dc2626] active:bg-[#b91c1c] transition-colors"
            style={{ minHeight: '52px' }}
          >
            Sell / Short
          </button>
        </div>
      </div>

      {/* ─── MOBILE: Full-screen Pair Selector Modal ─── */}
      {mobilePairModalOpen && (
        <div
          className="md:hidden fixed inset-0 z-[500] flex flex-col"
          style={{ background: '#141414' }}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="w-10 h-1 bg-[#3a3a3a] rounded-full" />
          </div>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] shrink-0">
            <span className="text-base font-bold text-[#F1F5F9]">Select Pair</span>
            <button
              onClick={() => setMobilePairModalOpen(false)}
              className="p-2 text-[#94A3B8] active:text-[#F1F5F9]"
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>
          {/* Pair selector content */}
          <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' as any }}>
            <TradingPairSelector
              selectedPair={selectedPair}
              onSelectPair={(pair) => { setSelectedPair(pair); setMobilePairModalOpen(false); }}
              liveStats={marketStats}
            />
          </div>
        </div>
      )}

      {/* ─── MOBILE: Trading Bottom Sheet ─── */}
      {mobileTradingSheetOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-[400] bg-black/60"
            onClick={() => setMobileTradingSheetOpen(false)}
          />
          {/* Sheet */}
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-[450] flex flex-col"
            style={{
              background: '#1a1a1a',
              borderTop: '1px solid #2a2a2a',
              borderRadius: '16px 16px 0 0',
              maxHeight: '85vh',
              paddingBottom: 'env(safe-area-inset-bottom)',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
            }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2 shrink-0">
              <div className="w-10 h-1 bg-[#3a3a3a] rounded-full" />
            </div>
            {/* Sheet header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-[#2a2a2a] shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-[#F1F5F9]">{selectedPair.symbol}</span>
                <span className="text-sm text-[#94A3B8]">${formattedPrice}</span>
              </div>
              <button
                onClick={() => setMobileTradingSheetOpen(false)}
                className="p-2 text-[#94A3B8]"
                style={{ minHeight: '44px', minWidth: '44px' }}
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            {/* Trading panel content */}
            <div
              className="flex-1 overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' as any }}
            >
              <TradingPanel
                symbol={selectedPair.symbol}
                currentPrice={displayPrice}
                externalPrice={orderBookPrice}
                externalSize={orderBookSize}
                externalSide={mobileTradingSide}
                externalOrderType={orderBookPrice ? 'Limit' : undefined}
                sltp={sltp}
                onSltpChange={setSltp}
                onTradePlaced={(marker) => { handleTradePlaced(marker); setMobileTradingSheetOpen(false); }}
                usdcBalance={usdcBalance}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Separate component to use the flash hook for the main price
function PriceCell({ price }: { price: string }) {
  const flash = useFlash(price);
  const textColor =
    flash === 'green' ? '#22C55E' :
    flash === 'red'? '#EF4444' : '#F1F5F9';

  return (
    <span
      className="text-base md:text-lg font-mono font-bold tabular-nums transition-colors duration-300"
      style={{ color: textColor }}
    >
      ${price}
    </span>
  );
}
